import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '~/components/core';
import { StrategyExecution } from '~/api-services/AIStrategyBuilderService/types';
import { executionColor, executionMessage } from '~/constants/strategyConstants';
import { FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import i18n from '~/i18n';
import { Theme } from '~/theme';

import { formatStrategyDateTime } from '../../../utils/format';
import ExecutionRow from './ExecutionRow';

type Props = {
    // Newest first, like the list itself.
    items: StrategyExecution[];
    code: string;
    expanded: boolean;
    onToggle: () => void;
};

export default function RunRow({ items, code, expanded, onToggle }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();

    if (items.length === 1) {
        return <ExecutionRow execution={items[0]} />;
    }

    const newest = items[0];
    const oldest = items[items.length - 1];

    return (
        <>
            <Pressable onPress={onToggle} style={styles.row}>
                <View style={styles.header}>
                    <View style={styles.outcome}>
                        <View style={[styles.dot, { backgroundColor: executionColor(theme, code) }]} />
                        <Text noTranslate style={styles.message}>
                            {executionMessage(code, newest.message, newest.context)}
                        </Text>
                    </View>
                    <Text noTranslate style={styles.time}>
                        {formatStrategyDateTime(newest.created)}
                    </Text>
                </View>
                <Text noTranslate style={styles.summary}>
                    {i18n.t('ai-strategy-exec-run', {
                        count: items.length,
                        since: formatStrategyDateTime(oldest.created),
                    })}
                </Text>
            </Pressable>
            {expanded
                ? items.map((item, index) => (
                      <View key={`${item.created}-${index}`} style={styles.nested}>
                          <ExecutionRow execution={item} />
                      </View>
                  ))
                : null}
        </>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        row: {
            paddingVertical: scaleSize(10),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: theme.tabIconDefault,
        },
        header: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        outcome: {
            flexDirection: 'row',
            alignItems: 'center',
            flexShrink: 1,
        },
        dot: {
            width: scaleSize(8),
            height: scaleSize(8),
            borderRadius: scaleSize(4),
            marginRight: scaleSize(8),
        },
        message: {
            color: theme.text,
            fontSize: FONT_SIZE_XS,
            flexShrink: 1,
        },
        time: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginLeft: scaleSize(8),
        },
        summary: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_XS,
            marginTop: scaleSize(4),
            marginLeft: scaleSize(16),
        },
        // Indented so an opened run reads as belonging to the row above it.
        nested: { paddingLeft: scaleSize(16) },
    });
